import { Position, PositionGroup } from '@statsbomb/parachute-types';
import { POSITION_TEMPLATE_MAP_MEN } from '@/consts/radar';
import { SYSTEM_WIDE_USER_ID } from '@/consts/userConfigs';
import { RadarData } from '@/types/radar';
import { UserConfigRadarTemplate } from '@/types/userConfigs';
import { isValidEnumValue } from '@/utils/predicates';
import { snakeToCamel } from '@/utils/queries';
import type { PositionMinutes, ProfileInfo } from '@statsbomb/kitbag-ai-summary';

export const isGkPosition = (position: string | undefined): boolean => position === Position.GK;

export const formatRadarValue = (value: number, isPercentage: boolean) => value * (isPercentage ? 100 : 1);

// StatsBomb (system-wide) profiles have translatable titles keyed under `templates.<title>`;
// user-owned and shared profiles use their raw, user-entered title as-is.
export const getRadarTemplateDisplayName = (template: UserConfigRadarTemplate, t: (key: string) => string): string => {
  const { title } = template.definition.titleSettings;
  return template.hudlUserId === SYSTEM_WIDE_USER_ID ? t(`templates.${title}`) : title;
};

// A position group with the positions of the template it came from. parachute-soccer-api derives every
// player template's `positions` from its `positionGroup`, so those positions are the group's members, the
// same ones the percentiles use; `memberPositions` is null when there is no template to take them from (or
// it lists none), so the summary sends no in-group minutes.
export type ResolvedPositionGroup = {
  positionGroup: PositionGroup;
  memberPositions: readonly string[] | null;
};

// The id of the default radar template for a position, or undefined for a missing or unknown position code
// (an API-sourced code that isn't a Position would otherwise index to undefined). The one lookup the radar's
// default template (defaultTemplateIdAtom) and the summary's group (resolvePositionGroup) share, so they
// can't drift apart on how they get from a position to a template.
export const defaultTemplateIdForPosition = (
  position: string | undefined,
  positionMap: Record<Position, string>,
): string | undefined => (isValidEnumValue(Position, position) ? positionMap[position] : undefined);

const templateMemberPositions = (template: UserConfigRadarTemplate) =>
  template.definition.positions?.length ? template.definition.positions : null;

const OUTFIELD_FALLBACK: ResolvedPositionGroup = { positionGroup: PositionGroup.OUTFIELD_A, memberPositions: null };

// The one lookup the radar and the AI summary share, each with its own selected template and its own
// player's position: the template's group, else the group of the default radar template for the
// position (a template without a group, e.g. Physical), else outfield_a.
// One map serves both genders: the men's and women's default templates share their position groups, and
// the API derives each template's positions from its group, so either map gives the same group and
// positions. radarTemplates holds both genders' templates.
export const resolvePositionGroup = (
  template: UserConfigRadarTemplate | undefined,
  position: string | undefined,
  radarTemplates: UserConfigRadarTemplate[],
): ResolvedPositionGroup => {
  const positionGroup = template?.definition.positionGroup;
  if (template && positionGroup) return { positionGroup, memberPositions: templateMemberPositions(template) };
  const defaultTemplateId = defaultTemplateIdForPosition(position, POSITION_TEMPLATE_MAP_MEN);
  if (!defaultTemplateId) return OUTFIELD_FALLBACK;

  const defaultTemplate = radarTemplates.find(radarTemplate => radarTemplate.configId === defaultTemplateId);
  const defaultGroup = defaultTemplate?.definition.positionGroup;
  if (!defaultTemplate || !defaultGroup) return OUTFIELD_FALLBACK;
  return { positionGroup: defaultGroup, memberPositions: templateMemberPositions(defaultTemplate) };
};

// The player's minutes at the group's positions, so the agent can tell a player assessed out of position
// (e.g. a centre back against a winger profile, which sums to 0). Undefined when there are no positions to
// sum over or no per-position minutes at all: the agent then bands on total minutes rather than reading a
// missing breakdown as zero minutes in the group.
export const sumPositionGroupMinutes = (
  positionMinutes: PositionMinutes[],
  memberPositions: readonly string[] | null,
): number | undefined => {
  if (!memberPositions || !positionMinutes.length) return undefined;
  const members = new Set(memberPositions);
  return positionMinutes.reduce((total, { position, minutes }) => (members.has(position) ? total + minutes : total), 0);
};

// Maps a selected radar template to the ProfileInfo the AI summary agent consumes: a display name
// plus the profile's own metric keys, the resolved position group, the player's minutes in it and the
// profile's positions (the evaluation lens, not the player's data). The group is resolved by the caller
// (resolvePositionGroup) so the agent uses it as received; positions stay alongside it until the
// agent drops its positions path.
// positionMinutes is the player's per-position minutes, nested here because it's only meaningful
// alongside a selected profile; omitted when empty so it isn't sent on the wire. Metric keys the user
// may not see (hiddenMetricKeys) are dropped so they aren't leaked to the agent via the profile.
export const toProfileInfo = (
  template: UserConfigRadarTemplate,
  t: (key: string) => string,
  positionMinutes: PositionMinutes[],
  hiddenMetricKeys: ReadonlySet<string>,
  { positionGroup, memberPositions }: ResolvedPositionGroup,
): ProfileInfo => {
  const positionGroupMinutes = sumPositionGroupMinutes(positionMinutes, memberPositions);
  return {
    name: getRadarTemplateDisplayName(template, t),
    metrics: template.definition.metrics.map(metric => metric.key).filter(key => !hiddenMetricKeys.has(key)),
    positionGroup,
    ...(positionGroupMinutes !== undefined ? { positionGroupMinutes } : {}),
    positions: template.definition.positions ?? [],
    ...(positionMinutes.length ? { positionMinutes } : {}),
  };
};

export const toRadarVisData = (t: (key: string) => string, items: RadarData[]) =>
  items.map(d => ({ ...d, label: t(`${snakeToCamel(d.key)}.name`) }));

export const toPercentileRadarData = (data: RadarData[], percentiles: Record<string, number>): RadarData[] =>
  data.map(item => ({
    ...item,
    value: percentiles[item.key] ?? 0,
    minValue: 0,
    maxValue: 100,
    decimalPlaces: 0,
  }));
